import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");

  // Stripe only ever redirects to this page when payment is complete.
  // We start in "confirmed" state immediately — polling only fetches the booking ref.
  const [booking, setBooking] = useState(null);
  const [refLoading, setRefLoading] = useState(true);

  useEffect(() => {
    if (!sessionId) { setRefLoading(false); return; }

    const poll = async (attempts = 0) => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.booking_ref) {
          setBooking(data);
          setRefLoading(false);
        } else if (attempts < 15) {
          // Keep polling up to 30 seconds — webhook can take a moment
          setTimeout(() => poll(attempts + 1), 2000);
        } else {
          // Still no ref after 30s — show success without ref
          setRefLoading(false);
        }
      } catch {
        if (attempts < 15) {
          setTimeout(() => poll(attempts + 1), 2000);
        } else {
          setRefLoading(false);
        }
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <Header />
      <div className="max-w-lg mx-auto px-4 py-20 text-center">

        {/* Always green — payment is confirmed */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-10 h-10 text-green-600" />
        </div>

        <h1 className="text-3xl font-bold text-[#2D3436] mb-2"
          style={{ fontFamily: "Fredoka, cursive" }}>
          Payment Successful!
        </h1>
        <p className="text-[#636E72] mb-8">
          {booking?.event_title
            ? <>Your booking for <strong className="text-[#2D3436]">{booking.event_title}</strong> is confirmed.</>
            : "Your booking is confirmed."}
        </p>

        {/* Booking reference card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          {refLoading ? (
            <div className="flex items-center justify-center gap-2 text-[#636E72] text-sm py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Fetching your booking reference…
            </div>
          ) : booking?.booking_ref ? (
            <>
              <p className="text-sm text-[#636E72] mb-1">Booking Reference</p>
              <p className="text-3xl font-bold text-[#ff3131] font-mono tracking-widest"
                style={{ fontFamily: "Fredoka, cursive" }}
                data-testid="booking-ref">
                {booking.booking_ref}
              </p>
              {booking.total_price > 0 && (
                <p className="text-sm text-[#636E72] mt-3">
                  Amount paid: <strong className="text-[#2D3436]">£{booking.total_price.toFixed(2)}</strong>
                </p>
              )}
              {booking.attendee_count > 1 && (
                <p className="text-sm text-[#636E72] mt-1">{booking.attendee_count} tickets</p>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-[#636E72]">
                Your booking reference will be sent to your email shortly.
              </p>
            </>
          )}
        </div>

        <p className="text-sm text-[#636E72] mb-8">
          We look forward to seeing you! If you have any questions contact us at{" "}
          <a href="mailto:admin@gym-tots.co.uk" className="text-[#ff3131] font-semibold">
            admin@gym-tots.co.uk
          </a>
        </p>

        <button
          onClick={() => nav("/events")}
          className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-8 py-3 rounded-full font-bold transition-colors"
          style={{ fontFamily: "Fredoka, cursive" }}
          data-testid="back-to-events-btn">
          Back to Events
        </button>
      </div>
      <Footer />
    </div>
  );
}
